/** Decision / question / permission cards (PRD §29): answer with an option, in your own words, or Allow/Deny. */
import { useState } from "react";
import type { Decision } from "../../api/types";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/toast";
import { Badge, Button } from "../../components/ui";
import { useApi, useMutation } from "../../data/hooks";
import { relative } from "../../lib/format";
import { DECISION_LABEL } from "../../lib/status";

export function DecisionCard({ decision: d }: { decision: Decision }) {
  const api = useApi();
  const toast = useToast();
  const [text, setText] = useState("");
  const [chosen, setChosen] = useState<string | null>(null);
  const answer = useMutation((a: { option_id?: string; text?: string }) => api.answerDecision(d.id, a), {
    invalidate: ["series", "decisions", "dashboard", "version", ...(d.series_id ? [`messages/${d.series_id}`] : [])],
    onError: (e) => { setChosen(null); toast.error(e, "Couldn't send the answer"); },
  });
  const permission = d.type === "permission_request";
  const label = DECISION_LABEL[d.type] ?? d.type;

  if (d.status !== "open") {
    const opt = d.options.find((o) => o.id === d.answer?.option_id);
    return (
      <div className="decision decision-done" aria-label={`${label}, answered`}>
        <Icon name="check" size={18} />
        <span><span className="muted">{label}:</span> {d.prompt} — <b>{[opt?.label ?? d.answer?.option_id, d.answer?.text].filter(Boolean).join(" · ") || d.status}</b></span>
      </div>
    );
  }

  const send = (a: { option_id?: string; text?: string }) => { setChosen(a.option_id ?? "text"); void answer.mutate(a); };
  const defaultLabel = d.default_answer?.option_id ? (d.options.find((o) => o.id === d.default_answer?.option_id)?.label ?? d.default_answer.option_id.replace(/_/g, " ")) : d.default_answer?.text;
  const options = permission && !d.options.length ? [{ id: "allow", label: "Allow" }, { id: "deny", label: "Deny" }] : d.options;

  return (
    <section className={`decision ${permission ? "decision-permission" : ""} ${d.blocking ? "is-blocking" : ""}`} aria-label={label}>
      <header className="decision-head">
        <Icon name={permission ? "lock" : d.type === "pronunciation_check" ? "volume" : "message"} size={20} />
        <span className="eyebrow">{label}{d.episode_sequence ? ` · Episode ${d.episode_sequence}` : ""}</span>
        {d.blocking ? <Badge tone="attention">Waiting on you</Badge> : <Badge tone="neutral">Not blocking</Badge>}
      </header>
      <p className="decision-prompt">{d.prompt}</p>
      {d.detail && (permission ? <pre className="decision-cmd">{d.detail}</pre> : <p className="muted">{d.detail}</p>)}

      {d.attachments.map((a) => (
        <div key={a.artifact_id} className="decision-audio">
          <audio controls preload="none" src={api.artifactUrl(a.artifact_id)} aria-label={a.filename ? `Play ${a.filename}` : "Play clip"} />
        </div>
      ))}

      <div className={`decision-options ${permission ? "row" : ""}`}>
        {options.map((o) => (
          <Button key={o.id} variant={permission ? (o.id === "deny" ? "danger" : "primary") : "secondary"} size="lg" block={!permission}
            busy={answer.pending && chosen === o.id} disabled={answer.pending} onClick={() => send({ option_id: o.id })} className="decision-option">
            <span className="opt-label">{o.label}</span>
            {o.description && <span className="opt-desc">{o.description}</span>}
          </Button>
        ))}
      </div>

      {!permission && (
        <form className="decision-text" onSubmit={(e) => { e.preventDefault(); if (text.trim()) send({ text: text.trim() }); }}>
          <label htmlFor={`dec-${d.id}`} className="sr-only">Answer in your own words</label>
          <input id={`dec-${d.id}`} value={text} onChange={(e) => setText(e.target.value)} placeholder="Or answer in your own words…" autoComplete="off" />
          <Button type="submit" variant="ghost" icon="send" aria-label="Send answer" disabled={!text.trim() || answer.pending} busy={answer.pending && chosen === "text"} />
        </form>
      )}
      <p className="muted small">
        Asked {relative(d.created_at)}
        {!d.blocking && defaultLabel ? ` · if you don't answer, the Studio uses the safe default: ${defaultLabel}` : ""}
        {permission ? " · denied automatically if unanswered" : ""}
      </p>
    </section>
  );
}
